import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { useAgentPanelContext } from '../AgentPanelContext'

const useStyles = makeStyles(() => ({
  choice: {
    padding: '10px 14px',
    border: '1px solid var(--color-ai-300)',
    backgroundColor: 'var(--color-neutral-050)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: 'var(--color-ai-500)',
      backgroundColor: 'var(--color-ai-050)',
      boxShadow: 'var(--shadow-sm)'
    }
  },
  choiceDisabled: {
    opacity: 0.5,
    cursor: 'default',
    pointerEvents: 'none'
  },
  choiceSelected: {
    borderColor: 'var(--color-ai-500)',
    backgroundColor: 'var(--color-ai-100)'
  },
  checkbox: {
    width: '16px',
    height: '16px',
    marginTop: '2px',
    borderRadius: '3px',
    border: '2px solid var(--color-ai-400)',
    transition: 'all 0.15s ease'
  },
  checkboxChecked: {
    backgroundColor: 'var(--color-ai-500)',
    borderColor: 'var(--color-ai-500)'
  },
  checkmark: {
    color: 'white',
    fontSize: '11px',
    lineHeight: 1
  },
  label: {
    color: 'var(--color-ai-700)'
  },
  description: {
    color: 'var(--color-neutral-600)',
    lineHeight: 1.4
  },
  submitButton: {
    padding: '6px 16px',
    color: 'white',
    backgroundColor: 'var(--color-ai-500)',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'var(--color-ai-600)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default'
    }
  }
}))

function MessageChoices ({ choices, multiselect }) {
  const classes = useStyles()
  const { addMessage, agentStatus } = useAgentPanelContext()
  const [submitted, setSubmitted] = useState(false)
  const [selectedValues, setSelectedValues] = useState(new Set())

  const isLocked = agentStatus === 'processing' || submitted

  const handleSingleSelect = useCallback((choice) => {
    if (isLocked) return
    setSubmitted(true)
    setSelectedValues(new Set([choice.value]))
    addMessage(choice.label)
  }, [addMessage, isLocked])

  const handleToggle = useCallback((choice) => {
    if (isLocked) return
    setSelectedValues(prev => {
      const next = new Set(prev)
      if (next.has(choice.value)) {
        next.delete(choice.value)
      } else {
        next.add(choice.value)
      }
      return next
    })
  }, [isLocked])

  const handleSubmitMulti = useCallback(() => {
    if (isLocked || selectedValues.size === 0) return
    setSubmitted(true)
    const selected = choices
      .filter(c => selectedValues.has(c.value))
      .map(c => c.label)
    addMessage(selected.join(', '))
  }, [addMessage, isLocked, selectedValues, choices])

  if (!Array.isArray(choices) || choices.length === 0) return null

  return (
    <div className='flex flex-col gap-2 my-2'>
      {choices.map((choice, index) => {
        const isSelected = selectedValues.has(choice.value)
        const className = [
          classes.choice,
          'flex items-start gap-2 radius-md',
          isLocked && !isSelected ? classes.choiceDisabled : '',
          isSelected ? classes.choiceSelected : ''
        ].filter(Boolean).join(' ')

        return (
          <div
            key={choice.value || index}
            className={className}
            onClick={() => multiselect ? handleToggle(choice) : handleSingleSelect(choice)}
          >
            {multiselect && (
              <div className={[classes.checkbox, 'flex items-center justify-center shrink-0', isSelected ? classes.checkboxChecked : ''].filter(Boolean).join(' ')}>
                {isSelected && <span className={classes.checkmark}>✓</span>}
              </div>
            )}
            <div className='flex flex-col gap-0.5 flex-1'>
              <span className={`${classes.label} font-size-sm font-weight-semibold`}>{choice.label}</span>
              {choice.description && (
                <span className={`${classes.description} font-size-xs`}>{choice.description}</span>
              )}
            </div>
          </div>
        )
      })}
      {multiselect && !submitted && (
        <div className='flex justify-end pt-1'>
          <button
            className={`${classes.submitButton} font-size-sm font-weight-semibold radius-md`}
            onClick={handleSubmitMulti}
            disabled={selectedValues.size === 0 || isLocked}
          >
            Confirm selection ({selectedValues.size})
          </button>
        </div>
      )}
    </div>
  )
}

MessageChoices.propTypes = {
  choices: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    description: PropTypes.string
  })).isRequired,
  multiselect: PropTypes.bool
}

export default MessageChoices
